// Upload or remove the signature image that the Documents section draws into
// the sign-off of letters, reports and sick notes. Same shape as
// UserProfilePic: pick a file, preview, save; closing with an unsaved file
// asks first.
import React, { useEffect, useState } from "react";
import { Modal, Alert, Button, Form, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import { uploadImage, updateUserSignatureURL } from "../../api";

export function UserSignature({ userId, signatureURL, show, onHide, onUpdated }) {
  const [closeAlert, setCloseAlert] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(signatureURL || "");
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [hiddenForSuccess, setHiddenForSuccess] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    if (show) {
      setHiddenForSuccess(false);
      setCloseAlert(false);
      setSelectedFile(null);
      setPreviewImage(signatureURL || "");
    }
  }, [show, signatureURL]);

  const handleClose = () => {
    if (!selectedFile) {
      onHide();
      return;
    }
    setCloseAlert(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      showToast("error", "The signature must be a PNG or JPEG image.");
      event.target.value = "";
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || submitting) return;
    const formData = new FormData();
    formData.append("image", selectedFile);
    formData.append("caption", "Signature");
    setSubmitting(true);
    try {
      const response = await uploadImage(formData);
      if (!response.imageUrl) throw new Error("No image URL returned from API.");
      await updateUserSignatureURL(userId, response.imageUrl);
      showToast("success", "Signature saved. It will appear on your letters and reports.");
      setHiddenForSuccess(true);
      onUpdated?.();
      onHide();
    } catch (error) {
      showToast("error", error.message || "Signature upload failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (removing) return;
    setRemoving(true);
    try {
      await updateUserSignatureURL(userId, null);
      showToast("success", "Signature removed.");
      setHiddenForSuccess(true);
      onUpdated?.();
      onHide();
    } catch (error) {
      showToast("error", error.message || "Could not remove the signature.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Modal show={show && !hiddenForSuccess} onHide={handleClose} size="lg" centered>
        <Modal.Header
          closeButton={!closeAlert}
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">Signature</Modal.Title>
        </Modal.Header>

        {closeAlert && (
          <Alert
            variant="info"
            className="d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0"
          >
            <span>Are you sure you want to close? The selected image will be discarded.</span>
            <div className="ms-auto d-flex gap-2">
              <OverlayTrigger placement="top" overlay={<Tooltip>Discard and close</Tooltip>}>
                <Button variant="outline-primary" onClick={() => { setCloseAlert(false); setSelectedFile(null); onHide(); }}>
                  <i className="fa-solid fa-check" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Keep editing</Tooltip>}>
                <Button variant="outline-secondary" onClick={() => setCloseAlert(false)}>
                  <i className="fa-solid fa-xmark" />
                </Button>
              </OverlayTrigger>
            </div>
          </Alert>
        )}

        <Modal.Body>
          <div className="posted-invoice-info mb-3">
            <i className="fa-solid fa-circle-info text-primary me-2" />
            Sign on white paper with a dark pen, photograph or scan it, and crop to the signature.
            It prints about 15 mm high above your typed name.
          </div>
          <div className="text-center mb-3">
            {previewImage ? (
              <img
                src={previewImage}
                alt="Signature preview"
                className="border rounded bg-white"
                style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain" }}
              />
            ) : (
              <div
                className="border rounded d-inline-flex align-items-center justify-content-center text-muted"
                style={{ width: 280, height: 100 }}
              >
                <i className="fa-light fa-signature fa-2x" />
              </div>
            )}
          </div>
          <Form.Group controlId="signatureFile" className="mb-2">
            <Form.Label>Upload signature image</Form.Label>
            <Form.Control type="file" accept="image/png,image/jpeg" onChange={handleFileChange} />
            <Form.Text>PNG or JPEG.</Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || submitting || removing}
            style={{ minWidth: 140 }}
          >
            {submitting ? <Spinner size="sm" animation="border" /> : (<><i className="fa-solid fa-upload me-2" />Save Signature</>)}
          </Button>
          {signatureURL && (
            <Button
              variant="outline-danger"
              onClick={handleRemove}
              disabled={submitting || removing}
              style={{ minWidth: 140 }}
            >
              {removing ? <Spinner size="sm" animation="border" /> : (<><i className="fa-solid fa-trash me-2" />Remove</>)}
            </Button>
          )}
          <Button variant="outline-secondary" onClick={handleClose} style={{ minWidth: 140 }}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <AppToast show={toast.show} onClose={hideToast} variant={toast.variant} message={toast.message} />
    </>
  );
}
